import React from 'react';
import { theme } from '../theme';
import { pillStyle } from './SecondaryAxisPills';

// <purpose>The shared LOOK of the "Texte & Vokabular" tab's two editing sections. Extracted from
// AntwortTexte when the vocabulary section joined it, so the two cannot drift into two dialects of
// the same card: one button voice, one feedback bubble, one pill, one reserved error zone.</purpose>
// <dependencies>theme.ts (colours/spacing); index.css (the stx-fadeIn keyframe).</dependencies>
// <constraints>Layout-Stability rule: everything that appears and disappears here does so INSIDE a
// pre-allocated box whose own footprint never changes — the error zone reserves its height whether
// or not it holds a bubble, and the badge row reserves its line whether or not a pill renders.
// Nothing in this file may collapse when its content is absent.</constraints>

export function btnStyle(kind: 'primary' | 'ghost' | 'danger'): React.CSSProperties {
  const base: React.CSSProperties = {
    padding: '7px 14px', borderRadius: 6, fontSize: 13, cursor: 'pointer',
    border: '1px solid transparent', fontWeight: 500,
  };
  if (kind === 'primary') return { ...base, background: theme.colors.navy, color: theme.colors.white };
  if (kind === 'danger') return { ...base, background: '#fef2f2', color: theme.colors.error };
  return { ...base, background: theme.colors.bgLight, color: theme.colors.navyMid };
}

export function feedbackStyle(isError: boolean): React.CSSProperties {
  return {
    padding: '8px 12px', borderRadius: 6, fontSize: 13,
    background: isError ? '#fbe9e9' : '#e9f6ec', color: isError ? '#b02a2a' : '#1f7a3d',
  };
}

// Same pill shape as EinstellungenSeite's badgeStyle(isAdmin) — one "badge-class element" idiom
// app-wide rather than a bespoke look per section. 'override' = this row carries a customization
// the tenant made; 'frozen' = BE-locked, view-only.
// Named `overlayChipStyle` since review round 1 (2026-09-08): `pillStyle` is now the house pill
// factory's name (SecondaryAxisPills, two registers), and two exported functions called the same
// thing in one app is exactly the collision that makes an import read as the wrong rule.
// Chip register sweep (Part C wave 2, 2026-09-09): both faces now draw their SHAPE from the
// house factory's `pillStyle('info', 'row')` — 'frozen' is otherwise fully info-register
// compliant (its face is Propercased through `pillFace` at each call site). 'override' keeps one
// documented exception on the FILL only: its own navy-filled look survives because that fill IS
// the "this row differs from the served default" signal, the same posture StufeChip/ZoneChip take
// on their own colour ramps (see the exception note in SecondaryAxisPills.tsx) — its face still
// goes through `pillFace` at the call site same as 'frozen', since "Angepasst · Rev N" is free
// prose built per row, not a fixed signal word.
export function overlayChipStyle(kind: 'override' | 'frozen' | 'suggested'): React.CSSProperties {
  // `shape` carries fontWeight 500 (the register's own weight), not this badge's old 600 — that
  // debold is ADOPTED, not a leftover (controller ruling, fix round 1, chip register sweep
  // review): the register's weight IS the harmonized outcome for a full-info-register face.
  // Do not silently "fix" this back to 600; both faces below are pinned at 500 in
  // pillRegisterSweep.test.tsx.
  const shape = pillStyle('info', 'row');
  if (kind === 'override') {
    return { ...shape, color: theme.colors.white, backgroundColor: theme.colors.navy, border: 'none' };
  }
  if (kind === 'suggested') {
    // The frozen shape's metrics, a DASHED border in the suggestion amber (never K4 orange) —
    // this is a proposal the admin has not yet confirmed, not a stated fact about the row.
    return {
      ...shape, color: theme.colors.suggestion,
      border: `1px dashed ${theme.colors.suggestion}`, backgroundColor: theme.colors.white,
    };
  }
  return shape;
}

// Inline, per-row save/reset/rollback error — same red as feedbackStyle(true) for visual
// continuity. Sits in a fixed-height reserved zone below the action row (in normal flow, never
// absolutely positioned over the gap): at this font-size a wrapping 2-line message needs more room
// than the inter-card gap has, and an overlay would spill into the next card's label.
function inlineFieldErrorStyle(): React.CSSProperties {
  return {
    padding: '6px 10px', borderRadius: 6, fontSize: 12, lineHeight: 1.4,
    background: '#fbe9e9', color: '#b02a2a',
    animation: 'stx-fadeIn 0.2s ease',
  };
}

// The zone renders unconditionally at its full reserved height; only the bubble inside it
// mounts/fades. A row with no failure path (a frozen one) simply never populates it and still
// occupies the same footprint as every other row.
export const InlineErrorZone: React.FC<{ rowKey: string; message?: string }> = ({ rowKey, message }) => (
  <div data-testid={`error-zone-${rowKey}`} style={{ minHeight: 48 }}>
    {message && <div style={inlineFieldErrorStyle()}>{message}</div>}
  </div>
);

// The one German sentence both sections use for a lost update — same structure, same recovery
// instruction, only the subject noun differs ("Der Text" for reply prose, "Die Bezeichnung" for a
// classification wording). A single flattened noun would have made both sentences vaguer than the
// thing they describe; sharing the SENTENCE while parameterising its subject keeps the two
// sections one voice without that cost.
export function staleConflictNotice(subjectDe: string): string {
  return `${subjectDe} wurde zwischenzeitlich geändert — neu geladen, bitte erneut prüfen.`;
}
